import React, { useState } from 'react';
import { Calendar, MapPin, Users, Send, CheckCircle, Plus, Trash2, Link, Clock } from 'lucide-react';
import { saveDataCollected } from './action';

interface Speaker {
  image_url: string;
  name: string;
  bio: string;
  organization: string;
}

interface EventWebinarInvitationProps {
  jsonData: {
    selected_layout: "event_webinar_invitation" | "thought_leadership";
    fields: any[];
  };
  threadId: string;
  userId: string;
  onConfirm?: () => void;
}

interface Field {
  field_name: string;
  field_type: string;
  required: boolean;
  default_value: string;
  description: string;
}

const EventWebinarInvitation = (props: EventWebinarInvitationProps) => {
  const { jsonData, threadId, userId } = props;
  
  const [formData, setFormData] = useState<Record<string, any>>(() => {
    const initialData: Record<string, any> = {};
    const fields = jsonData?.fields;
    
    // Track field type counts for unique keys
    const fieldTypeCounts: Record<string, number> = {};

    fields?.forEach((field: any, index: number) => {
      const fieldType = field.field_type;
      
      // Initialize counter for this field type if not exists
      if (!fieldTypeCounts[fieldType]) {
        fieldTypeCounts[fieldType] = 0;
      }
      
      // Create unique key using field type and index
      const uniqueKey = `${fieldType}_${fieldTypeCounts[fieldType]}`;
      
      if (fieldType === 'speaker') {
        initialData[uniqueKey] = [];
      } else if (fieldType === 'cta') {
        initialData[uniqueKey] = { url: '', text: '' };
      } else if (fieldType === 'date') {
        initialData[uniqueKey] = field.default_value ?? "";
        initialData[`${uniqueKey}_time`] = "";
      } else {
        initialData[uniqueKey] = field.default_value ?? "";
      }
      
      // Increment counter for this field type
      fieldTypeCounts[fieldType]++;
    });

    return initialData;
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCTAChange = (ctaKey: string, field: 'url' | 'text', value: string) => {
    setFormData(prev => ({
      ...prev,
      [ctaKey]: {
        ...prev[ctaKey],
        [field]: value
      }
    }));
  };

  const getIconForFieldType = (fieldType: string) => {
    switch (fieldType) {
      case 'cta':
        return <Send className="w-2 h-2 mr-1 text-blue-600" />;
      case 'date':
        return <Calendar className="w-2 h-2 mr-1 text-blue-600" />;
      case 'location':
        return <MapPin className="w-2 h-2 mr-1 text-blue-600" />;
      case 'speaker':
        return <Users className="w-2 h-2 mr-1 text-blue-600" />;
      default:
        return null;
    }
  };

  const getPlaceholderForFieldType = (fieldType: string) => {
    switch (fieldType) {
      case 'date':
        return 'e.g., 20 April 2026';
      case 'location':
        return 'e.g., The Grand Convention Center, Innovation Hall';
      default:
        return '';
    }
  };

  const formatTimeTo12Hour = (time: string): string => {
    if (!time) return '';
    
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Function to combine date and time into a single string
  const combineDateAndTime = (date: string, time: string): string => {
    if (!date) return '';
    if (!time) return date;
    const formattedTime = formatTimeTo12Hour(time);
    return `${date} at ${formattedTime}`;
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Track field type counts for validation
    const fieldTypeCounts: Record<string, number> = {};

    // Validate required fields
    const requiredFields = jsonData?.fields.filter((field: any) => field.required);
    for (let i = 0; i < requiredFields.length; i++) {
      const field = requiredFields[i];
      const fieldType = field.field_type;
      
      if (!fieldTypeCounts[fieldType]) {
        fieldTypeCounts[fieldType] = 0;
      }
      
      const uniqueKey = `${fieldType}_${fieldTypeCounts[fieldType]}`;
      
      if (fieldType === 'speaker') {
        if (!formData[uniqueKey] || formData[uniqueKey].length === 0) {
          setError(`${field.field_name} is required. Please add at least one speaker.`);
          setIsSubmitting(false);
          return;
        }
        // Validate each speaker has required fields
        for (let j = 0; j < formData[uniqueKey].length; j++) {
          const speaker = formData[uniqueKey][j];
          if (!speaker.name?.trim()) {
            setError(`Speaker ${j + 1}: Name is required`);
            setIsSubmitting(false);
            return;
          }
          if (!speaker.bio?.trim()) {
            setError(`Speaker ${j + 1}: BIO is required`);
            setIsSubmitting(false);
            return;
          }
        }
      } else if (fieldType === 'cta') {
        if (!formData[uniqueKey]?.url?.trim() || !formData[uniqueKey]?.text?.trim()) {
          setError(`${field.field_name}: Both URL and Text are required`);
          setIsSubmitting(false);
          return;
        }
      } else if (fieldType === 'date') {
        if (!formData[uniqueKey]?.trim()) {
          setError(`${field.field_name} is required`);
          setIsSubmitting(false);
          return;
        }
      } else {
        if (!formData[uniqueKey]?.trim()) {
          setError(`${field.field_name} is required`);
          setIsSubmitting(false);
          return;
        }
      }
      
      fieldTypeCounts[fieldType]++;
    }

    try {
      // Reset field type counts for data preparation
      const prepFieldTypeCounts: Record<string, number> = {};

      const dataFields = jsonData.fields.map((field: any) => {
        const fieldType = field.field_type;
        
        if (!prepFieldTypeCounts[fieldType]) {
          prepFieldTypeCounts[fieldType] = 0;
        }
        
        const uniqueKey = `${fieldType}_${prepFieldTypeCounts[fieldType]}`;
        let data;
        
        if (fieldType === 'speaker') {
          data = formData[uniqueKey];
        } else if (fieldType === 'cta') {
          data = formData[uniqueKey];
        } else if (fieldType === 'date') {
          const dateValue = formData[uniqueKey] || '';
          const timeValue = formData[`${uniqueKey}_time`] || '';
          data = combineDateAndTime(dateValue, timeValue);
        } else {
          data = formData[uniqueKey];
        }
        
        prepFieldTypeCounts[fieldType]++;
        
        return {
          field_name: field.field_name,
          field_type: field.field_type,
          data: data,
          description: field.description,
        };
      });

      // Replace this with your actual saveDataCollected function call
      // console.log('Submitting data:', {
      //   userId,
      //   threadId,
      //   payload: {
      //     selected_layout: jsonData.selected_layout,
      //     data_fields: dataFields
      //   }
      // });

      // Uncomment when ready to use
      const res = await saveDataCollected(userId, threadId, {
        selected_layout: jsonData.selected_layout,
        data_fields: dataFields
      });
      
      if (res?.success === true) {
        setIsSuccess(true);
        props.onConfirm?.()
      } else {
        setError("Failed to save data");
      }

      // Simulate success for demo
      setTimeout(() => {
        setIsSuccess(true);
        setIsSubmitting(false);
      }, 1000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate invitation');
      setIsSubmitting(false);
    }
  };

  const renderCTAField = (field: Field, index: number) => {
    const ctaKey = `cta_${index}`;
    
    return (
      <div key={`cta_${index}`}>
        <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
          {getIconForFieldType(field.field_type)}
          {field.field_name}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        <div className="space-y-3">
          <div>
            <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
              <Link className="w-2 h-2 mr-1" />
              CTA URL
            </label>
            <input
              type="url"
              value={formData[ctaKey]?.url || ''}
              onChange={(e) => handleCTAChange(ctaKey, 'url', e.target.value)}
              placeholder="https://example.com/register"
              className="w-full p-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none focus:border-transparent transition"
            />
          </div>
          
          <div>
            <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
              <Send className="w-2 h-2 mr-1" />
              CTA Text
            </label>
            <input
              type="text"
              value={formData[ctaKey]?.text || ''}
              onChange={(e) => handleCTAChange(ctaKey, 'text', e.target.value)}
              placeholder="e.g., Register Now, Join Us, RSVP Today"
              className="w-full p-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none focus:border-transparent transition"
            />
          </div>
        </div>
        
        <p className="text-xs text-gray-500 mt-2">{field.description}</p>
      </div>
    );
  };

  const renderSpeakerField = (field: Field, index: number) => {
    const speakerKey = `speaker_${index}`;
    
    return (
      <div key={`speaker_${index}`}>
        <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
          {getIconForFieldType(field.field_type)}
          {field.field_name}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        <div className="space-y-3">
          {formData[speakerKey]?.map((speaker: Speaker, speakerIndex: number) => (
            <div key={speakerIndex} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-sm font-semibold text-gray-700">Speaker {speakerIndex + 1}</h4>
                {formData[speakerKey].length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setFormData(prev => ({
                        ...prev,
                        [speakerKey]: prev[speakerKey].filter((_: any, i: number) => i !== speakerIndex)
                      }));
                    }}
                    className="text-red-500 hover:text-red-700 transition"
                  >
                    <Trash2 className="w-2 h-2" />
                  </button>
                )}
              </div>
              
              <div className="space-y-2">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Image URL</label>
                  <input
                    type="url"
                    value={speaker.image_url}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        [speakerKey]: prev[speakerKey].map((s: Speaker, i: number) => 
                          i === speakerIndex ? { ...s, image_url: e.target.value } : s
                        )
                      }));
                    }}
                    placeholder="https://example.com/image.jpg"
                    className="w-full p-1 border text-xs border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>
                
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Name *</label>
                  <input
                    type="text"
                    value={speaker.name}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        [speakerKey]: prev[speakerKey].map((s: Speaker, i: number) => 
                          i === speakerIndex ? { ...s, name: e.target.value } : s
                        )
                      }));
                    }}
                    placeholder="Dr. Jane Smith"
                    className="w-full p-1 border text-xs border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>
                
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">BIO *</label>
                  <textarea
                    value={speaker.bio}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        [speakerKey]: prev[speakerKey].map((s: Speaker, i: number) => 
                          i === speakerIndex ? { ...s, bio: e.target.value } : s
                        )
                      }));
                    }}
                    placeholder="AI Expert with 15+ years of experience..."
                    rows={2}
                    className="w-full p-1 border text-xs border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition resize-none"
                  />
                </div>
                
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Organization</label>
                  <input
                    type="text"
                    value={speaker.organization}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        [speakerKey]: prev[speakerKey].map((s: Speaker, i: number) => 
                          i === speakerIndex ? { ...s, organization: e.target.value } : s
                        )
                      }));
                    }}
                    placeholder="Tech Innovation Labs"
                    className="w-full p-1 border text-xs border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 transition"
                  />
                </div>
              </div>
            </div>
          ))}
          
          <button
            type="button"
            onClick={() => {
              setFormData(prev => ({
                ...prev,
                [speakerKey]: [...(prev[speakerKey] || []), { image_url: '', name: '', bio: '', organization: '' }]
              }));
            }}
            className="w-full p-1 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition flex items-center justify-center text-sm font-medium text-gray-600 hover:text-blue-600"
          >
            <Plus className="w-2 h-2 mr-1" />
            Add Speaker
          </button>
        </div>
        
        <p className="text-xs text-gray-500 mt-2">{field.description}</p>
      </div>
    );
  };

  const renderDateField = (field: Field, index: number) => {
    const dateKey = `date_${index}`;
    
    return (
      <div key={`date_${index}`}>
        <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
          {getIconForFieldType(field.field_type)}
          {field.field_name}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        
        <div className="grid grid-cols-1 gap-2">
          <div>
            <label className="flex items-center text-xs font-medium text-gray-600 mb-1">
              <Calendar className="w-2 h-2 mr-1" />
              Date
            </label>
            <input
              type="date"
              name={dateKey}
              value={formData[dateKey] || ''}
              onChange={handleInputChange}
              placeholder={getPlaceholderForFieldType(field.field_type)}
              className="w-full p-1 border text-xs border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            />
          </div>
        </div>
        
        {formData[dateKey] && formData[`${dateKey}_time`] && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-700">
              <strong>Preview:</strong> {combineDateAndTime(formData[dateKey], formData[`${dateKey}_time`])}
            </p>
          </div>
        )}
        
        <p className="text-xs text-gray-500 mt-2">{field.description}</p>
      </div>
    );
  };

  const renderField = (field: Field, fieldIndex: number) => {
    // Count how many of this field type we've seen so far
    const fieldsBeforeThis = jsonData.fields.slice(0, fieldIndex).filter(f => f.field_type === field.field_type).length;
    const uniqueKey = `${field.field_type}_${fieldsBeforeThis}`;
    
    if (field.field_type === 'speaker') {
      return renderSpeakerField(field, fieldsBeforeThis);
    }
    
    if (field.field_type === 'cta') {
      return renderCTAField(field, fieldsBeforeThis);
    }
    
    if (field.field_type === 'date') {
      return renderDateField(field, fieldsBeforeThis);
    }
    
    return (
      <div key={uniqueKey}>
        <label className="flex items-center text-sm font-semibold text-gray-700 mb-2">
          {getIconForFieldType(field.field_type)}
          {field.field_name}
          {field.required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input
          type={field.field_type === 'location' ? 'text' : field.field_type}
          name={uniqueKey}
          value={formData[uniqueKey] || ''}
          onChange={handleInputChange}
          placeholder={getPlaceholderForFieldType(field.field_type)}
          className="w-full p-1 border text-xs border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
        />
        <p className="text-xs text-gray-500 mt-1">{field.description}</p>
      </div>
    );
  };

  return (
    <div className="h-[90vh] max-w-xl border rounded-xl border-gray-300 overflow-y-auto">
      <div className="max-w-4xl mx-auto">
        {isSuccess ? (
          <div className="flex flex-col items-center justify-center p-6">
            <CheckCircle className="w-20 h-20 text-green-600 mb-4" />
            <h2 className="text-lg font-semibold text-gray-800">
              Data collected successfully
            </h2>
            <p className="text-sm text-gray-600 mt-1 text-center">
              Thank you! Your information has been saved successfully.
            </p>
          </div>
        ) : (
          <div className="overflow-hidden">
            {/* Header */}
            <div className="p-2 border-b border-gray-300">
              <h1 className="text-lg font-bold text-gray-900">
                {(jsonData?.selected_layout ?? "")
                  .split("_")
                  .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ")}
              </h1>
              <p className="text-gray-600 text-sm">Create a professional webinar invitation</p>
            </div>

            {/* Dynamic Form */}
            <div className="p-2">
              <div className="space-y-3">
                {jsonData?.fields?.map((field: any, index: number) => renderField(field, index))}

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className=" bg-purple-900 text-white font-semibold py-1 text-sm px-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-lg"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin h-2 w-2 mr-2" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Generating Invitation...
                    </span>
                  ) : (
                    'Generate Invitation'
                  )}
                </button>
              </div>
            </div>

            {/* Info Card */}
            <div className="mt-2 p-2 border-t border-gray-200 bg-gray-50">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">How it works:</h3>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• Fill in all required fields marked with *</li>
                <li>• For date fields, you can optionally add a time</li>
                <li>• Add speakers with their details using the "Add Speaker" button</li>
                <li>• Provide both URL and text for the call-to-action</li>
                <li>• Click "Generate Invitation" to create a professional email</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventWebinarInvitation;